import { Routes } from "@blitzjs/next"
import { useMutation } from "@blitzjs/rpc"
import { Membership, User } from "@superwall/db"
import login from "app/auth/mutations/login"
import { Login } from "app/auth/validations"
import { Form, FORM_ERROR } from "app/core/components/form/Form"
import { LabeledTextField } from "app/core/components/form/LabeledTextField"
import LabeledTextFieldV2 from "app/core/components/form/LabeledTextFieldV2"
import { toast } from "app/core/components/hints"
import Card from "app/core/components/layout/Card"
import useUserAgent from "app/core/hooks/useUserAgent"
import { ColorBlur } from "app/splash/components/ColorBlur"
import { AuthenticationError, setCookie } from "blitz"
import Link from "next/link"
import { Title } from "pages/forgot-password"
import { FC, useRef } from "react"
import { Field } from "react-final-form"
import tw from "tailwind-styled-components"

// import { defaultUser } from "@superwall/db"
const defaultUser = {
  email: "team@paywalrus.com",
  password: "password1",
}

type LoginFormProps = {
  onSuccess?: ({
    selectOrganization,
    user,
  }: {
    selectOrganization: boolean
    user: Omit<User, "hashedPassword"> & { memberships: Membership[] }
  }) => void
}

export const CardBlur = tw.div`
relative bg-grayscale-lighter bg-opacity-10 border border-white border-1 border-opacity-10 shadow-2xl

p-6 rounded-[18px]
md:p-9 md:rounded-[25px]
`

const FullPageFormHeader: FC = ({}) => {
  const { isMobileClient } = useUserAgent()
  return (
    <div>
      <div className="text-left mb-4">
        <Title>Log In</Title>
        <div className="">
          <span className="text-sm">
            {!isMobileClient && (
              <Link passHref={true} href={Routes.SignupPage()}>
                <span className="opacity-80">{`No account? `}</span>
                <span className="text-brand-primary font-medium">{`Sign up →`}</span>
              </Link>
            )}
          </span>
        </div>
      </div>
    </div>
  )
}

export const LoginForm = (props: LoginFormProps) => {
  const [loginMutation] = useMutation(login)

  const ref = useRef<HTMLInputElement>(null)

  return (
    <CardBlur>
      <Card title={<FullPageFormHeader />}>
        <ColorBlur fixed={true} opacity={0.05}></ColorBlur>
        <Form
          submitText="Log in →"
          buttonFill={true}
          validateOnBlur={false}
          buttonPosition="end"
          buttonProps={{
            variant: "juicy",
            className: "w-auto",
          }}
          schema={Login}
          initialValues={
            (false && process.env.NODE_ENV === "development") || process.env.HEROKU_PR_NUMBER
              ? defaultUser
              : { email: "", password: "" }
          }
          onSubmit={async (values) => {
            try {
              const { failedInvite, selectOrganization, user } = await loginMutation(values)
              if (failedInvite) {
                toast.error({ content: "Unable to accept invite" })
              }

              props.onSuccess?.({ selectOrganization, user })
            } catch (error) {
              if (error instanceof AuthenticationError) {
                return {
                  [FORM_ERROR]:
                    "Sorry, those credentials are invalid" +
                    (process.env.NODE_ENV === "production"
                      ? ""
                      : ". If 'admin@superwall.com' with password 'password1' isn't working, ou may need to run `yarn db:setup` from apps/web"),
                }
              } else {
                return {
                  [FORM_ERROR]:
                    "Sorry, we had an unexpected error. Please try again. - " + error.toString(),
                }
              }
            }
          }}
        >
          <div className="pt-2 flex flex-col gap-3">
            <LabeledTextFieldV2
              ref={ref}
              name="email"
              label="Email"
              placeholder="Enter your Email..."
            />
            <LabeledTextFieldV2
              style={{ borderColor: "rgba(255,255,255,0.1)" }}
              name="password"
              label="Password"
              placeholder="Enter a Password..."
              type="password"
            />
          </div>
          <div className="w-full flex flex-row justify-end mb-4 relative">
            <Field name="email">
              {(props) => (
                <Link
                  passHref={true}
                  href={Routes.ForgotPasswordPage({ email: props.input.value })}
                  legacyBehavior={true}
                >
                  <a className="text-right">
                    <span className="flex text-sm self-start absolute left-0 pr-2 py-2 top-[1rem] items-center opacity-60">
                      {`Forgot Password?`}
                    </span>
                  </a>
                </Link>
              )}
            </Field>
          </div>
        </Form>

        {/* <div style={{ marginTop: "1rem" }}>
        Or <Link href={Routes.SignupPage()}>Sign Up</Link>
      </div> */}
      </Card>
    </CardBlur>
  )
}

export default LoginForm
